import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { usePageAuthorization } from '../hooks/usePageAuthorization';
import { CreateEventTaskModal } from '../components/CreateEventTaskModal';

// ── TypeScript Interfaces ─────────────────────────────────────

type SubcommitteeRole = 'Chair' | 'Member' | 'Volunteer';
type TaskStatus = 'ToDo' | 'InProgress' | 'Done';
type SortKey = 'name' | 'memberCount' | 'taskCount';
type SortDirection = 'asc' | 'desc';
type TabKey = 'details' | 'members' | 'tasks' | 'events';

interface SubcommitteeMemberInfo {
  userId: string;
  role: string;
  user: {
    id: string;
    fullName: string;
    email: string;
    role: string;
  };
}

interface SubcommitteeTask {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  status: TaskStatus;
  isOverdue: boolean;
  createdAt: string;
}

interface MemberAffiliationInfo {
  memberId: string;
  subcommitteeId: string;
  role: string;
  member: {
    id: string;
    userId: string | null;
    firstName: string;
    lastName: string;
  };
}

interface SubCommitteeWithCounts {
  id: string;
  name: string;
  description: string;
  preLoaded: boolean;
  createdAt: string;
  members: SubcommitteeMemberInfo[];
  memberAffiliations: MemberAffiliationInfo[];
  tasks: SubcommitteeTask[];
  _count: {
    tasks: number;
    members: number;
    memberAffiliations: number;
  };
}

interface MemberAffiliationResponse {
  memberId: string;
  subcommitteeId: string;
  role: SubcommitteeRole;
  assignedAt: string;
  member: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string | null;
    status: string;
    chapterAffiliation: string | null;
  };
}

interface MemberOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 1.5rem 0' },
  filterBar: { display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' as const, marginBottom: '1.5rem' },
  input: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const, width: '100%' },
  select: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: '#fff', boxSizing: 'border-box' as const },
  btn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  btnSecondary: { padding: '0.5rem 1rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  btnDanger: { padding: '0.4rem 0.75rem', backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' },
  btnSmall: { padding: '0.3rem 0.6rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', border: 'none', borderRadius: '3px' },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' },
  loading: { textAlign: 'center' as const, padding: '3rem', color: '#666', fontSize: '1rem' },
  emptyState: { textAlign: 'center' as const, padding: '2rem', color: '#666', fontSize: '0.9rem' },
  card: { border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '0.75rem', backgroundColor: '#fff', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  cardHeader: { padding: '1rem 1.25rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'background-color 0.15s' },
  cardHeaderExpanded: { padding: '1rem 1.25rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f0f7f2', borderBottom: '1px solid #e0e0e0' },
  cardName: { fontSize: '1rem', fontWeight: 600, color: '#0f2d1f', margin: 0 },
  cardDesc: { fontSize: '0.8rem', color: '#666', margin: '0.25rem 0 0 0', maxWidth: '500px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  cardBadges: { display: 'flex', gap: '0.5rem', alignItems: 'center' },
  badge: { display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600, color: '#fff' },
  expandedContent: { padding: '1.25rem', minWidth: '600px' },
  tabBar: { display: 'flex', gap: '0', borderBottom: '2px solid #e0e0e0', marginBottom: '1rem' },
  tab: { padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500, color: '#666', borderBottom: '2px solid transparent', marginBottom: '-2px', transition: 'color 0.15s' },
  tabActive: { padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#1a6b3c', borderBottom: '2px solid #1a6b3c', marginBottom: '-2px' },
  detailGrid: { display: 'grid', gridTemplateColumns: '200px 200px 200px', gap: '1rem' },
  detailLabel: { fontWeight: 600, fontSize: '0.8rem', color: '#666', marginBottom: '0.15rem' },
  detailValue: { fontSize: '0.9rem', color: '#333' },
  memberRow: { display: 'grid', gridTemplateColumns: '200px 100px 120px auto', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.75rem', borderBottom: '1px solid #f0f0f0' },
  memberInfo: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  taskCard: { padding: '0.75rem', border: '1px solid #e0e0e0', borderRadius: '6px', marginBottom: '0.5rem', backgroundColor: '#fafafa' },
  taskTitle: { fontSize: '0.9rem', fontWeight: 500, color: '#333' },
  taskMeta: { display: 'flex', gap: '0.75rem', alignItems: 'center', marginTop: '0.25rem', fontSize: '0.8rem', color: '#666' },
  overdue: { color: '#d32f2f', fontWeight: 600 },
  modalOverlay: { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: '#fff', borderRadius: '8px', padding: '1.5rem', width: '100%', maxWidth: '450px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' },
  modalTitle: { fontSize: '1.1rem', fontWeight: 600, color: '#0f2d1f', marginBottom: '1rem' },
  field: { marginBottom: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontWeight: 600, fontSize: '0.85rem', color: '#333' },
};

const ROLE_COLORS: Record<SubcommitteeRole, string> = {
  Chair: '#c9a84c',
  Member: '#1a6b3c',
  Volunteer: '#4a90d9',
};

const STATUS_COLORS: Record<TaskStatus, string> = {
  ToDo: '#888',
  InProgress: '#c9a84c',
  Done: '#1a6b3c',
};


// ── Filter/Sort Function ──────────────────────────────────────

export function filterSubcommittees(
  subcommittees: SubCommitteeWithCounts[],
  searchQuery: string,
  sortBy: SortKey,
  sortDirection: SortDirection,
): SubCommitteeWithCounts[] {
  const query = searchQuery.toLowerCase().trim();

  let filtered = subcommittees;
  if (query.length > 0) {
    filtered = subcommittees.filter(
      (sc) =>
        sc.name.toLowerCase().includes(query) ||
        sc.description.toLowerCase().includes(query),
    );
  }

  const sorted = [...filtered].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'memberCount':
        comparison = a._count.memberAffiliations - b._count.memberAffiliations;
        break;
      case 'taskCount':
        comparison = a._count.tasks - b._count.tasks;
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  return sorted;
}

// ── AddAffiliationModal ───────────────────────────────────────

function AddAffiliationModal({
  subcommitteeId,
  existingMemberIds,
  onClose,
  onAdded,
}: {
  subcommitteeId: string;
  existingMemberIds: string[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [selectedMemberLabel, setSelectedMemberLabel] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [role, setRole] = useState<SubcommitteeRole>('Member');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchMembers() {
      try {
        const allMembers = await apiClient<MemberOption[]>('/members');
        const available = allMembers.filter(
          (m) => !existingMemberIds.includes(m.id),
        );
        setMembers(available);
      } catch {
        setError('Failed to load members.');
      } finally {
        setLoading(false);
      }
    }
    fetchMembers();
  }, [existingMemberIds]);

  const filteredMembers = searchTerm.trim().length > 0
    ? members.filter((m) => {
        const q = searchTerm.toLowerCase();
        return (
          m.firstName.toLowerCase().includes(q) ||
          m.lastName.toLowerCase().includes(q) ||
          m.email.toLowerCase().includes(q) ||
          `${m.firstName} ${m.lastName}`.toLowerCase().includes(q)
        );
      })
    : members;

  function handleSelectMember(m: MemberOption) {
    setSelectedMemberId(m.id);
    setSelectedMemberLabel(`${m.firstName} ${m.lastName}`);
    setSearchTerm(`${m.firstName} ${m.lastName}`);
    setShowDropdown(false);
  }

  function handleSearchChange(val: string) {
    setSearchTerm(val);
    setShowDropdown(true);
    if (!val.trim()) {
      setSelectedMemberId('');
      setSelectedMemberLabel('');
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedMemberId) return;
    setSubmitting(true);
    setError('');
    try {
      await apiClient(`/subcommittees/${subcommitteeId}/affiliations`, {
        method: 'POST',
        body: { memberId: selectedMemberId, role },
      });
      onAdded();
    } catch (err: unknown) {
      const apiErr = err as { statusCode?: number; message?: string };
      if (apiErr.statusCode === 409) {
        setError('This member is already affiliated with this sub-committee.');
      } else if (apiErr.statusCode === 404) {
        setError('Member not found.');
      } else {
        setError(apiErr.message || 'Failed to add affiliation.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.modalTitle}>Add Member Affiliation</h3>
        {error && <div style={styles.error}>{error}</div>}
        {loading ? (
          <div style={styles.loading}>Loading members...</div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={styles.field}>
              <label style={styles.label}>Member</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  style={{ ...styles.input, fontSize: '16px' }}
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => setShowDropdown(true)}
                  aria-label="Search for a member"
                  autoComplete="off"
                />
                {selectedMemberId && (
                  <div style={{ fontSize: '0.72rem', color: '#1a6b3c', marginTop: '3px', fontWeight: 500 }}>
                    Selected: {selectedMemberLabel}
                  </div>
                )}
                {showDropdown && searchTerm.trim().length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: '180px', overflowY: 'auto', backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '0 0 6px 6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10 }}>
                    {filteredMembers.length === 0 ? (
                      <div style={{ padding: '10px 12px', fontSize: '0.8rem', color: '#999' }}>No members found</div>
                    ) : (
                      filteredMembers.slice(0, 20).map((m) => (
                        <div
                          key={m.id}
                          onClick={() => handleSelectMember(m)}
                          style={{ padding: '8px 12px', fontSize: '0.82rem', cursor: 'pointer', borderBottom: '1px solid #f5f5f5', backgroundColor: m.id === selectedMemberId ? '#f0fdf4' : '#fff' }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = m.id === selectedMemberId ? '#f0fdf4' : '#fff')}
                        >
                          <div style={{ fontWeight: 500 }}>{m.firstName} {m.lastName}</div>
                          <div style={{ fontSize: '0.72rem', color: '#666' }}>{m.email}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Role</label>
              <select
                style={{ ...styles.select, width: '100%' }}
                value={role}
                onChange={(e) => setRole(e.target.value as SubcommitteeRole)}
              >
                <option value="Chair">Chair</option>
                <option value="Member">Member</option>
                <option value="Volunteer">Volunteer</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                type="submit"
                style={{ ...styles.btn, opacity: submitting || !selectedMemberId ? 0.6 : 1 }}
                disabled={submitting || !selectedMemberId}
              >
                {submitting ? 'Adding...' : 'Add Member'}
              </button>
              <button type="button" style={styles.btnSecondary} onClick={onClose}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── MembersTab ────────────────────────────────────────────────

function MembersTab({ subcommitteeId, isChair }: { subcommitteeId: string; isChair: boolean }) {
  const [affiliations, setAffiliations] = useState<MemberAffiliationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchAffiliations = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient<MemberAffiliationResponse[]>(
        `/subcommittees/${subcommitteeId}/affiliations`,
      );
      setAffiliations(data);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load affiliations.');
    } finally {
      setLoading(false);
    }
  }, [subcommitteeId]);

  useEffect(() => {
    fetchAffiliations();
  }, [fetchAffiliations]);

  async function handleRemove(memberId: string) {
    try {
      await apiClient(`/subcommittees/${subcommitteeId}/affiliations/${memberId}`, {
        method: 'DELETE',
      });
      fetchAffiliations();
    } catch (err: unknown) {
      const apiErr = err as { statusCode?: number; message?: string };
      if (apiErr.statusCode === 404) {
        setError('Affiliation not found.');
      } else {
        setError(apiErr.message || 'Failed to remove affiliation.');
      }
      fetchAffiliations();
    }
  }

  if (loading) return <div style={styles.loading}>Loading members...</div>;

  return (
    <div>
      {error && <div style={styles.error}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <span style={{ fontSize: '0.85rem', color: '#666' }}>
          {affiliations.length} member{affiliations.length !== 1 ? 's' : ''} affiliated
        </span>
        {isChair && (
          <button style={styles.btn} onClick={() => setShowAddModal(true)}>
            + Add Member
          </button>
        )}
      </div>

      {affiliations.length === 0 ? (
        <div style={styles.emptyState}>No members affiliated yet.</div>
      ) : (
        <div>
          <div style={{ ...styles.memberRow, borderBottom: '2px solid #e0e0e0', fontWeight: 600, fontSize: '0.8rem', color: '#666', textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>
            <span>Name</span>
            <span>Role</span>
            <span>Assigned</span>
            <span style={{ textAlign: 'right' as const }}>Actions</span>
          </div>
          {affiliations.map((aff) => (
          <div key={aff.memberId} style={styles.memberRow}>
            <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>
              {aff.member.firstName} {aff.member.lastName}
            </span>
            <span
              style={{
                ...styles.badge,
                backgroundColor: ROLE_COLORS[aff.role],
              }}
            >
              {aff.role}
            </span>
            <span style={{ fontSize: '0.75rem', color: '#999' }}>
              {new Date(aff.assignedAt).toLocaleDateString()}
            </span>
            <div style={{ textAlign: 'right' as const }}>
              {isChair && (
                <button
                  style={styles.btnDanger}
                  onClick={() => handleRemove(aff.memberId)}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
        </div>
      )}

      {showAddModal && (
        <AddAffiliationModal
          subcommitteeId={subcommitteeId}
          existingMemberIds={affiliations.map((a) => a.memberId)}
          onClose={() => setShowAddModal(false)}
          onAdded={() => {
            setShowAddModal(false);
            fetchAffiliations();
          }}
        />
      )}
    </div>
  );
}

// ── TasksTab ──────────────────────────────────────────────────

function TasksTab({ tasks: initialTasks, subcommitteeId, isChair }: { tasks: SubcommitteeTask[]; subcommitteeId: string; isChair: boolean }) {
  const [tasks, setTasks] = useState<SubcommitteeTask[]>(initialTasks);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', dueDate: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState<string | null>(null);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [updatingTaskId, setUpdatingTaskId] = useState<string | null>(null);
  const [showEventTaskModal, setShowEventTaskModal] = useState(false);

  // Event Tasks from /event-tasks/subcommittee/:id
  const [eventTasks, setEventTasks] = useState<{ id: string; title: string; description: string; status: string; priority: string; dueDate: string; category: string; event?: { name: string } | null; assignee?: { firstName: string; lastName: string } | null }[]>([]);
  const [eventTasksLoading, setEventTasksLoading] = useState(true);

  // Sync with parent when initialTasks changes
  useEffect(() => {
    setTasks(initialTasks);
  }, [initialTasks]);

  // Fetch event tasks for this subcommittee
  useEffect(() => {
    fetchEventTasks();
  }, [subcommitteeId]);

  async function fetchEventTasks() {
    setEventTasksLoading(true);
    try {
      const data = await apiClient<typeof eventTasks>(`/event-tasks/subcommittee/${subcommitteeId}`);
      setEventTasks(data);
    } catch {
      setEventTasks([]);
    } finally {
      setEventTasksLoading(false);
    }
  }

  async function handleCreateTask(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title || !form.description || !form.dueDate) return;
    setSubmitting(true);
    setError('');
    try {
      const created = await apiClient<SubcommitteeTask>('/tasks', {
        method: 'POST',
        body: {
          title: form.title,
          description: form.description,
          subcommitteeId,
          dueDate: new Date(form.dueDate).toISOString(),
        },
      });
      setTasks((prev) => [...prev, created]);
      setForm({ title: '', description: '', dueDate: '' });
      setShowForm(false);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to create task.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleStatusChange(taskId: string, newStatus: TaskStatus) {
    setUpdatingTaskId(taskId);
    setError('');
    try {
      const updated = await apiClient<SubcommitteeTask>(`/tasks/${taskId}`, {
        method: 'PATCH',
        body: { status: newStatus },
      });
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: updated.status } : t)));
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to update status.');
    } finally {
      setUpdatingTaskId(null);
    }
  }

  async function handleSaveDescription(taskId: string) {
    setUpdatingTaskId(taskId);
    setError('');
    try {
      await apiClient(`/tasks/${taskId}`, {
        method: 'PATCH',
        body: { description: descriptionDraft },
      });
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, description: descriptionDraft } : t)));
      setEditingDescription(null);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to update description.');
    } finally {
      setUpdatingTaskId(null);
    }
  }

  function toggleExpand(taskId: string) {
    if (expandedTaskId === taskId) {
      setExpandedTaskId(null);
      setEditingDescription(null);
    } else {
      setExpandedTaskId(taskId);
      setEditingDescription(null);
    }
  }

  function startEditDescription(task: SubcommitteeTask) {
    setEditingDescription(task.id);
    setDescriptionDraft(task.description);
  }

  return (
    <div>
      {error && <div style={styles.error}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <span style={{ fontSize: '0.85rem', color: '#666' }}>
          {tasks.length} task{tasks.length !== 1 ? 's' : ''}
        </span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {isChair && (
            <button style={styles.btn} onClick={() => setShowEventTaskModal(true)}>
              + Event Task
            </button>
          )}
          {isChair && (
            <button style={styles.btn} onClick={() => setShowForm(!showForm)}>
              {showForm ? 'Cancel' : '+ Add Task'}
            </button>
          )}
        </div>
      </div>

      {/* Create Event Task Modal — pre-fills subcommittee */}
      <CreateEventTaskModal
        isOpen={showEventTaskModal}
        onClose={() => setShowEventTaskModal(false)}
        onSuccess={() => setShowEventTaskModal(false)}
        preSelectedSubcommitteeId={subcommitteeId}
      />

      {showForm && isChair && (
        <form onSubmit={handleCreateTask} style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={styles.label}>Title *</label>
            <input
              type="text"
              style={styles.input}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Task title"
              maxLength={200}
              required
            />
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={styles.label}>Description *</label>
            <textarea
              style={{ ...styles.input, minHeight: '60px', resize: 'vertical' as const }}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Task description"
              maxLength={2000}
              required
            />
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={styles.label}>Due Date *</label>
            <input
              type="date"
              style={styles.input}
              value={form.dueDate}
              onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
              required
            />
          </div>
          <button
            type="submit"
            style={{ ...styles.btn, opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
            disabled={submitting}
          >
            {submitting ? 'Creating...' : 'Create Task'}
          </button>
        </form>
      )}

      {tasks.length === 0 ? (
        <div style={styles.emptyState}>No tasks assigned to this sub-committee.</div>
      ) : (
        tasks.map((task) => {
          const isOverdue = task.status !== 'Done' && new Date(task.dueDate) < new Date();
          const isExpanded = expandedTaskId === task.id;
          const isUpdating = updatingTaskId === task.id;

          return (
            <div key={task.id} style={{ ...styles.taskCard, cursor: 'pointer' }}>
              <div onClick={() => toggleExpand(task.id)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={styles.taskTitle}>{task.title}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span
                      style={{
                        ...styles.badge,
                        backgroundColor: STATUS_COLORS[task.status],
                      }}
                    >
                      {task.status}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#999' }}>{isExpanded ? '▲' : '▼'}</span>
                  </div>
                </div>
                <div style={styles.taskMeta}>
                  <span>Due: {new Date(task.dueDate).toLocaleDateString()}</span>
                  {isOverdue && <span style={styles.overdue}>⚠ Overdue</span>}
                </div>
              </div>

              {isExpanded && (
                <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }} onClick={(e) => e.stopPropagation()}>
                  {/* Status Controls — Chair only */}
                  {isChair && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#666', marginBottom: '0.4rem' }}>Change Status:</div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {(['ToDo', 'InProgress', 'Done'] as TaskStatus[]).map((s) => (
                        <button
                          key={s}
                          onClick={() => handleStatusChange(task.id, s)}
                          disabled={task.status === s || isUpdating}
                          style={{
                            padding: '0.3rem 0.6rem',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            border: 'none',
                            borderRadius: '4px',
                            cursor: task.status === s || isUpdating ? 'not-allowed' : 'pointer',
                            backgroundColor: task.status === s ? STATUS_COLORS[s] : '#e5e7eb',
                            color: task.status === s ? '#fff' : '#374151',
                            opacity: isUpdating ? 0.6 : 1,
                          }}
                        >
                          {s === 'ToDo' ? 'To Do' : s === 'InProgress' ? 'In Progress' : 'Done'}
                        </button>
                      ))}
                    </div>
                  </div>
                  )}

                  {/* Description */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#666' }}>Description:</span>
                      {isChair && editingDescription !== task.id && (
                        <button
                          onClick={() => startEditDescription(task)}
                          style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                    {editingDescription === task.id ? (
                      <div>
                        <textarea
                          style={{ ...styles.input, minHeight: '80px', resize: 'vertical' as const, marginBottom: '0.5rem' }}
                          value={descriptionDraft}
                          onChange={(e) => setDescriptionDraft(e.target.value)}
                          maxLength={2000}
                        />
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => handleSaveDescription(task.id)}
                            disabled={isUpdating}
                            style={{ ...styles.btn, fontSize: '0.75rem', padding: '0.3rem 0.6rem', opacity: isUpdating ? 0.6 : 1 }}
                          >
                            {isUpdating ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingDescription(null)}
                            style={{ ...styles.btnSecondary, fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.85rem', color: '#374151', whiteSpace: 'pre-wrap' as const, backgroundColor: '#f9fafb', padding: '0.5rem 0.75rem', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                        {task.description || 'No description'}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

// ── EventsTab ─────────────────────────────────────────────────

function EventsTab() {
  return (
    <div style={styles.emptyState}>
      Event management is coming soon.
    </div>
  );
}


// ── EditSubcommitteeModal ─────────────────────────────────────

function EditSubcommitteeModal({
  subcommittee,
  onClose,
  onSaved,
}: {
  subcommittee: SubCommitteeWithCounts;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(subcommittee.name);
  const [description, setDescription] = useState(subcommittee.description || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await apiClient(`/subcommittees/${subcommittee.id}`, {
        method: 'PATCH',
        body: { name: name.trim(), description: description.trim() || undefined },
      });
      onSaved();
    } catch (err: unknown) {
      const apiErr = err as { statusCode?: number; message?: string };
      if (apiErr.statusCode === 409) {
        setError('A sub-committee with this name already exists.');
      } else {
        setError(apiErr.message || 'Failed to update sub-committee.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.modalTitle}>Edit Sub-Committee</h3>
        {error && <div style={styles.error}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label style={styles.label}>Name *</label>
            <input
              type="text"
              style={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sub-committee name"
              maxLength={100}
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Description</label>
            <input
              type="text"
              style={styles.input}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description"
              maxLength={500}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button
              type="submit"
              style={{ ...styles.btn, opacity: submitting || !name.trim() ? 0.6 : 1 }}
              disabled={submitting || !name.trim()}
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
            <button type="button" style={styles.btnSecondary} onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── SubcommitteeCard ──────────────────────────────────────────

function SubcommitteeCard({
  subcommittee,
  isExpanded,
  onToggle,
  canExpand,
  isChairOfThis,
  onEdit,
  onDelete,
}: {
  subcommittee: SubCommitteeWithCounts;
  isExpanded: boolean;
  onToggle: (id: string) => void;
  canExpand: boolean;
  isChairOfThis: boolean;
  onEdit: (subcommittee: SubCommitteeWithCounts) => void;
  onDelete: (subcommittee: SubCommitteeWithCounts) => void;
}) {
  const [activeTab, setActiveTab] = useState<TabKey>('details');

  // Reset to details tab when card is expanded
  useEffect(() => {
    if (isExpanded) {
      setActiveTab('details');
    }
  }, [isExpanded]);

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'details', label: 'Details' },
    { key: 'members', label: 'Members' },
    { key: 'tasks', label: 'Tasks' },
    { key: 'events', label: 'Events' },
  ];

  return (
    <div style={styles.card}>
      <div
        style={isExpanded ? styles.cardHeaderExpanded : { ...styles.cardHeader, cursor: canExpand ? 'pointer' : 'default', opacity: canExpand ? 1 : 0.8 }}
        onClick={() => onToggle(subcommittee.id)}
      >
        <div>
          <h3 style={styles.cardName}>{subcommittee.name}</h3>
          {subcommittee.description && (
            <p style={styles.cardDesc}>{subcommittee.description}</p>
          )}
        </div>
        <div style={styles.cardBadges}>
          <span style={{ ...styles.badge, backgroundColor: '#1a6b3c' }}>
            {subcommittee._count.memberAffiliations} member{subcommittee._count.memberAffiliations !== 1 ? 's' : ''}
          </span>
          <span style={{ ...styles.badge, backgroundColor: '#666' }}>
            {subcommittee._count.tasks} task{subcommittee._count.tasks !== 1 ? 's' : ''}
          </span>
          {isChairOfThis && (
            <>
              <button
                style={{ ...styles.btnSmall, backgroundColor: '#e0e0e0', color: '#333' }}
                title="Edit sub-committee"
                onClick={(e) => { e.stopPropagation(); onEdit(subcommittee); }}
              >
                ✏️
              </button>
              <button
                style={{ ...styles.btnSmall, backgroundColor: '#fdecea', color: '#d32f2f' }}
                title="Delete sub-committee"
                onClick={(e) => { e.stopPropagation(); onDelete(subcommittee); }}
              >
                🗑️
              </button>
            </>
          )}
          {canExpand ? (
            <span style={{ fontSize: '1rem', color: '#999' }}>
              {isExpanded ? '▲' : '▼'}
            </span>
          ) : (
            <span style={{ fontSize: '0.8rem', color: '#999' }} title="You must be a member of this committee to view details">
              🔒
            </span>
          )}
        </div>
      </div>

      {isExpanded && (
        <div style={styles.expandedContent}>
          {/* Tab Bar */}
          <div style={styles.tabBar}>
            {tabs.map((tab) => (
              <div
                key={tab.key}
                style={activeTab === tab.key ? styles.tabActive : styles.tab}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </div>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'details' && (
            <div>
              <div style={styles.detailGrid}>
                <div>
                  <div style={styles.detailLabel}>Name</div>
                  <div style={styles.detailValue}>{subcommittee.name}</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Created</div>
                  <div style={styles.detailValue}>
                    {new Date(subcommittee.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Pre-Loaded</div>
                  <div style={styles.detailValue}>
                    {subcommittee.preLoaded ? 'Yes' : 'No'}
                  </div>
                </div>
              </div>
              {subcommittee.description && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={styles.detailLabel}>Description</div>
                  <div style={styles.detailValue}>{subcommittee.description}</div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'members' && (
            <MembersTab subcommitteeId={subcommittee.id} isChair={isChairOfThis} />
          )}

          {activeTab === 'tasks' && (
            <TasksTab tasks={subcommittee.tasks} subcommitteeId={subcommittee.id} isChair={isChairOfThis} />
          )}

          {activeTab === 'events' && <EventsTab />}
        </div>
      )}
    </div>
  );
}

// ── Main Page Component ───────────────────────────────────────

export function SubcommitteeDirectoryPage() {
  const { user: authUser } = useAuth();
  const { canEdit } = usePageAuthorization('committees');
  const [subcommittees, setSubcommittees] = useState<SubCommitteeWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [userAffiliatedSubcommitteeIds, setUserAffiliatedSubcommitteeIds] = useState<Set<string>>(new Set());
  const [userChairedSubcommitteeIds, setUserChairedSubcommitteeIds] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
  const [showMyOnly, setShowMyOnly] = useState(false);

  const isAdmin = authUser?.role === 'Admin' || authUser?.role === 'Organizer';
  const [userIsChair, setUserIsChair] = useState(false);

  // Create sub-committee form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createSubmitting, setCreateSubmitting] = useState(false);

  // Edit sub-committee modal state
  const [editingSubcommittee, setEditingSubcommittee] = useState<SubCommitteeWithCounts | null>(null);

  // Debounce search query
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  const fetchSubcommittees = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient<SubCommitteeWithCounts[]>('/subcommittees');
      setSubcommittees(data);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load sub-committees.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubcommittees();
  }, [fetchSubcommittees]);

  // Fetch current user's sub-committee affiliations
  useEffect(() => {
    if (!authUser?.sub || subcommittees.length === 0) return;
    // Derive user's affiliations from the subcommittees data (memberAffiliations includes member.userId)
    const affiliatedIds = new Set<string>();
    const chairedIds = new Set<string>();
    let hasChair = false;

    for (const sc of subcommittees) {
      if (!sc.memberAffiliations) continue;
      const myAff = sc.memberAffiliations.find((a: any) => a.member?.userId === authUser.sub);
      if (myAff) {
        affiliatedIds.add(sc.id);
        if (myAff.role === 'Chair') {
          chairedIds.add(sc.id);
          hasChair = true;
        }
      }
    }

    setUserAffiliatedSubcommitteeIds(affiliatedIds);
    setUserChairedSubcommitteeIds(chairedIds);
    setUserIsChair(hasChair);
  }, [authUser?.sub, subcommittees]);

  async function handleCreateSubcommittee(e: React.FormEvent) {
    e.preventDefault();
    if (!createName.trim()) return;
    setCreateSubmitting(true);
    setError('');
    try {
      await apiClient('/subcommittees', {
        method: 'POST',
        body: {
          name: createName.trim(),
          description: createDescription.trim() || undefined,
        },
      });
      setCreateName('');
      setCreateDescription('');
      setShowCreateForm(false);
      await fetchSubcommittees();
    } catch (err: unknown) {
      const apiErr = err as { message?: string; statusCode?: number };
      if (apiErr.statusCode === 409) {
        setError('A sub-committee with this name already exists.');
      } else {
        setError(apiErr.message || 'Failed to create sub-committee.');
      }
    } finally {
      setCreateSubmitting(false);
    }
  }

  function toggleExpanded(id: string) {
    // Check if user can expand this committee
    // Admin bypasses all restrictions; Chair of any committee can expand all committees
    const canExpand = isAdmin || userIsChair || userAffiliatedSubcommitteeIds.has(id);
    if (!canExpand) return; // Silently prevent expansion for non-members
    setExpandedId((prev) => (prev === id ? null : id));
  }

  function handleEditSubcommittee(subcommittee: SubCommitteeWithCounts) {
    setEditingSubcommittee(subcommittee);
  }

  async function handleDeleteSubcommittee(subcommittee: SubCommitteeWithCounts) {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${subcommittee.name}"? This action cannot be undone.`,
    );
    if (!confirmed) return;
    try {
      await apiClient(`/subcommittees/${subcommittee.id}`, { method: 'DELETE' });
      if (expandedId === subcommittee.id) setExpandedId(null);
      await fetchSubcommittees();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to delete sub-committee.');
    }
  }

  const filteredSubcommittees = filterSubcommittees(
    subcommittees,
    debouncedQuery,
    sortBy,
    sortDirection,
  );

  if (loading) {
    return (
      <div style={styles.page}>
        <h1 style={styles.title}>Sub-Committees</h1>
        <div style={styles.loading}>Loading sub-committees...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ ...styles.title, margin: 0 }}>Sub-Committees</h1>
        {canEdit && (isAdmin || userIsChair) && (
          <button style={styles.btn} onClick={() => setShowCreateForm(!showCreateForm)}>
            {showCreateForm ? 'Cancel' : '+ Add Sub-Committee'}
          </button>
        )}
      </div>

      {error && (
        <div style={styles.error}>
          {error}{' '}
          <button style={styles.btn} onClick={fetchSubcommittees}>
            Retry
          </button>
        </div>
      )}

      {/* Create Sub-Committee Form */}
      {showCreateForm && (
        <div style={{ marginBottom: '1.5rem', padding: '1.25rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
          <h3 style={{ color: '#0f2d1f', fontSize: '1rem', marginBottom: '1rem' }}>Create New Sub-Committee</h3>
          <form onSubmit={handleCreateSubcommittee}>
            <div style={styles.field}>
              <label style={styles.label}>Name *</label>
              <input
                style={{ ...styles.input, maxWidth: '400px' }}
                type="text"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="e.g., Decorations Committee"
                maxLength={100}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Description</label>
              <input
                style={{ ...styles.input, maxWidth: '600px' }}
                type="text"
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                placeholder="Brief description of the committee's purpose"
                maxLength={500}
              />
            </div>
            <button type="submit" style={styles.btn} disabled={createSubmitting || !createName.trim()}>
              {createSubmitting ? 'Creating...' : 'Create Sub-Committee'}
            </button>
          </form>
        </div>
      )}

      {/* Search & Filter Bar */}
      <div style={styles.filterBar}>
        <input
          type="text"
          style={{ ...styles.input, width: '300px', maxWidth: '300px', flex: 'none' }}
          placeholder="Search by name or description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select
          style={styles.select}
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortKey)}
        >
          <option value="name">Sort by Name</option>
          <option value="memberCount">Sort by Member Count</option>
          <option value="taskCount">Sort by Task Count</option>
        </select>
        <button
          style={styles.btnSecondary}
          onClick={() =>
            setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
          }
        >
          {sortDirection === 'asc' ? '↑ Asc' : '↓ Desc'}
        </button>
        <button
          style={{ ...styles.btnSecondary, ...(showMyOnly ? { backgroundColor: '#1a6b3c', color: '#fff', borderColor: '#1a6b3c' } : {}) }}
          onClick={() => setShowMyOnly(!showMyOnly)}
        >
          📂 My Subcommittees
        </button>
        <button
          style={{ ...styles.btnSecondary, ...(viewMode === 'table' ? { backgroundColor: '#1a6b3c', color: '#fff', borderColor: '#1a6b3c' } : {}) }}
          onClick={() => setViewMode(viewMode === 'card' ? 'table' : 'card')}
        >
          {viewMode === 'card' ? '☰ Table' : '▦ Cards'}
        </button>
      </div>

      {/* Sub-Committee List */}
      {filteredSubcommittees.filter(sc => !showMyOnly || userAffiliatedSubcommitteeIds.has(sc.id)).length === 0 ? (
        <div style={styles.emptyState}>
          {showMyOnly ? 'You are not a member of any sub-committees.' : 'No sub-committees match your search.'}
        </div>
      ) : viewMode === 'table' ? (
        <div style={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px', marginTop: '0.5rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f8f8' }}>
                <th style={{ textAlign: 'left', padding: '0.7rem 1rem', fontWeight: 600, borderBottom: '2px solid #1a6b3c', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '0.7rem 1rem', fontWeight: 600, borderBottom: '2px solid #1a6b3c', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Description</th>
                <th style={{ textAlign: 'center', padding: '0.7rem 1rem', fontWeight: 600, borderBottom: '2px solid #1a6b3c', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Members</th>
                <th style={{ textAlign: 'center', padding: '0.7rem 1rem', fontWeight: 600, borderBottom: '2px solid #1a6b3c', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Tasks</th>
                <th style={{ textAlign: 'center', padding: '0.7rem 1rem', fontWeight: 600, borderBottom: '2px solid #1a6b3c', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>My Role</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubcommittees
                .filter(sc => !showMyOnly || userAffiliatedSubcommitteeIds.has(sc.id))
                .map(sc => (
                <tr key={sc.id} style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer', transition: 'background 0.15s' }} onClick={() => {
                    const canEditThis = isAdmin || userChairedSubcommitteeIds.has(sc.id);
                    if (canEditThis) {
                      handleEditSubcommittee(sc);
                    } else {
                      toggleExpanded(sc.id);
                    }
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0faf4'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                >
                  <td style={{ padding: '0.7rem 1rem', fontWeight: 600, color: '#0f2d1f' }}>{sc.name}</td>
                  <td style={{ padding: '0.7rem 1rem', color: '#666', fontSize: '0.8rem' }}>{sc.description || '—'}</td>
                  <td style={{ padding: '0.7rem 1rem', textAlign: 'center' }}>{sc._count.memberAffiliations}</td>
                  <td style={{ padding: '0.7rem 1rem', textAlign: 'center' }}>{sc._count.tasks}</td>
                  <td style={{ padding: '0.7rem 1rem', textAlign: 'center' }}>
                    {(() => {
                      const myAffiliation = sc.memberAffiliations?.find(a => a.member?.userId === authUser?.sub);
                      if (myAffiliation) {
                        const role = myAffiliation.role;
                        const color = role === 'Chair' ? '#e65100' : role === 'Volunteer' ? '#1565c0' : '#2e7d32';
                        const bg = role === 'Chair' ? '#fff3e0' : role === 'Volunteer' ? '#e3f2fd' : '#e8f5e9';
                        return <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: '10px', backgroundColor: bg, color, fontWeight: 600 }}>{role}</span>;
                      }
                      return '—';
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        filteredSubcommittees
          .filter(sc => !showMyOnly || userAffiliatedSubcommitteeIds.has(sc.id))
          .map((sc) => (
          <SubcommitteeCard
            key={sc.id}
            subcommittee={sc}
            isExpanded={expandedId === sc.id}
            onToggle={toggleExpanded}
            canExpand={isAdmin || userIsChair || userAffiliatedSubcommitteeIds.has(sc.id)}
            isChairOfThis={isAdmin || userChairedSubcommitteeIds.has(sc.id)}
            onEdit={handleEditSubcommittee}
            onDelete={handleDeleteSubcommittee}
          />
        ))
      )}

      {/* Edit Sub-Committee Modal */}
      {editingSubcommittee && (
        <EditSubcommitteeModal
          subcommittee={editingSubcommittee}
          onClose={() => setEditingSubcommittee(null)}
          onSaved={() => {
            setEditingSubcommittee(null);
            fetchSubcommittees();
          }}
        />
      )}
    </div>
  );
}
